import React, { useState } from 'react';
import { 
  HiOutlineQuestionMarkCircle, 
  HiOutlineUser, 
  HiOutlineClipboardCheck,
  HiOutlineDocument,
  HiOutlineCalendar,
  HiOutlineGlobeAlt,
  HiOutlineBriefcase,
  HiOutlineChartBar,
  HiOutlineSearch,
  HiOutlineCog,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineExclamationCircle
} from 'react-icons/hi';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import useAdmin from '../../hooks/useAdmin';
import AdminViewHelp from './AdminViewHelp';
import './Help.scss';
import './AdminViewHelp.scss';

const Help = () => {
  const { isAdmin } = useAdmin();
  const [activeSection, setActiveSection] = useState('getting-started');
  
  const handleSectionClick = (section) => {
    setActiveSection(section);
  };
  
  return (
    <div className="help-page">
      <div className="help-header">
        <HiOutlineQuestionMarkCircle size={32} />
        <h1>Help Center</h1>
      </div>
      
      <div className="help-description">
        <p>
          Welcome to the MyelinZ PPC Portal help center. Find comprehensive guides and information about using all features of the platform.
        </p>
      </div>
      
      {/* Admin-specific help component */}
      {isAdmin && <AdminViewHelp />}
      
      <div className="help-navigation">
        <button 
          className={activeSection === 'getting-started' ? 'active' : ''} 
          onClick={() => handleSectionClick('getting-started')}
        >
          Getting Started
        </button>
        <button 
          className={activeSection === 'profile' ? 'active' : ''} 
          onClick={() => handleSectionClick('profile')}
        >
          Profile Management
        </button>
        <button 
          className={activeSection === 'documents' ? 'active' : ''} 
          onClick={() => handleSectionClick('documents')}
        >
          Documents & Payslips
        </button>
        <button 
          className={activeSection === 'tasks' ? 'active' : ''} 
          onClick={() => handleSectionClick('tasks')}
        >
          Tasks & Performance
        </button>
        <button 
          className={activeSection === 'requests' ? 'active' : ''} 
          onClick={() => handleSectionClick('requests')}
        >
          Requests & Claims
        </button>
        {isAdmin && (
          <button 
            className={activeSection === 'admin' ? 'active' : ''} 
            onClick={() => handleSectionClick('admin')}
          >
            Admin Tools
          </button>
        )}
        <button 
          className={activeSection === 'support' ? 'active' : ''} 
          onClick={() => handleSectionClick('support')}
        >
          Support
        </button>
      </div>
      
      <div className="help-content">
        {activeSection === 'getting-started' && (
        <div className="help-section">
          <h2>Getting Started</h2>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineUser className="card-icon" />
                <h3>Navigating the Portal</h3>
              </div>
              <div className="help-card-content">
                <p>The MyelinZ PPC Portal provides a comprehensive set of tools to manage your work life.</p>
                <ul>
                  <li><strong>Sidebar Navigation:</strong> Use the sidebar to access different sections of the portal.</li>
                  <li><strong>Profile:</strong> View and update your personal information.</li>
                  <li><strong>Dashboard:</strong> Get a quick overview of your tasks, upcoming reviews, and notifications.</li>
                  <li><strong>Mobile View:</strong> On smaller screens, use the menu button to access the navigation sidebar.</li>
                </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>You can collapse the sidebar by clicking the arrow icon at the bottom to get more screen space.</p>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineCog className="card-icon" />
                <h3>Account Settings</h3>
              </div>
              <div className="help-card-content">
                <p>Managing your account settings ensures your profile is up-to-date and secure.</p>
                <ul>
                  <li><strong>Password:</strong> Change your password regularly for security.</li>
                  <li><strong>Notifications:</strong> Configure which notifications you want to receive.</li>
                  <li><strong>Theme:</strong> Choose between light and dark mode for your preferred viewing experience.</li>
                </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>For security reasons, the system will prompt you to change your password every 90 days.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'profile' && (
          <div className="help-section">
            <h2>Profile Management</h2>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineUser className="card-icon" />
                <h3>Updating Your Profile</h3>
              </div>
              <div className="help-card-content">
                <p>Keep your profile information current to ensure HR has accurate records.</p>
                <ul>
                  <li><strong>Personal Information:</strong> Update your contact details, address, and emergency contacts.</li>
                  <li><strong>Profile Picture:</strong> Upload a professional photo for your profile.</li>
                  <li><strong>Skills & Bio:</strong> Showcase your professional skills and write a brief biography.</li>
                </ul>
                <div className="help-steps">
                  <h4>How to update your profile:</h4>
                  <ol>
                    <li>Navigate to "My Profile" in the sidebar</li>
                    <li>Click on the "Edit" button next to the section you want to update</li>
                    <li>Make your changes and click "Save"</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineUser className="card-icon" />
                <h3>Bank & Declaration Details</h3>
              </div>
              <div className="help-card-content">
                <p>Manage your banking and personal declaration information for payroll and HR purposes.</p>
                <ul>
                  <li><strong>Bank Details:</strong> Update your account information for salary deposits.</li>
                  <li><strong>Declarations:</strong> Provide educational qualifications, work experience, and any medical conditions.</li>
                </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>Your bank details are encrypted and securely stored. Only authorized HR personnel can access this information.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'documents' && (
          <div className="help-section">
            <h2>Documents & Payslips</h2>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineDocument className="card-icon" />
                <h3>Accessing Your Documents</h3>
              </div>
              <div className="help-card-content">
                <p>View and download important documents related to your employment.</p>
                <ul>
                  <li><strong>ID & Visa:</strong> Access your identification and visa documents.</li>
                  <li><strong>Employment Documents:</strong> View your employment contract and related documents.</li>
                  <li><strong>Miscellaneous:</strong> Access other important documents shared by HR.</li>
                </ul>
                <div className="help-steps">
                  <h4>How to download a document:</h4>
                  <ol>
                    <li>Navigate to the "Documents" section</li>
                    <li>Browse through the document categories</li>
                    <li>Click the "Download" button next to the document you want to save</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <FaFileInvoiceDollar className="card-icon" />
                <h3>Viewing Payslips</h3>
              </div>
              <div className="help-card-content">
                <p>Access and download your monthly salary statements.</p>
                <ul>
                  <li><strong>Monthly Payslips:</strong> View payslips organized by month and year.</li>
                  <li><strong>Download Format:</strong> Payslips are available as PDF files for easy printing.</li>
                  <li><strong>History:</strong> Access your complete payslip history for the duration of your employment.</li>
                </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>You will receive a notification when a new payslip is available for viewing.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'tasks' && (
          <div className="help-section">
            <h2>Tasks & Performance</h2>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineClipboardCheck className="card-icon" />
                <h3>Managing Tasks</h3>
              </div>
              <div className="help-card-content">
                <p>View and manage your assigned tasks and projects.</p>
                <ul>
                  <li><strong>Task List:</strong> View all your current tasks and their deadlines.</li>
                  <li><strong>Task Status:</strong> Update the status of tasks as you progress.</li>
                  <li><strong>Priorities:</strong> Tasks are color-coded based on priority levels.</li>
                </ul>
                <div className="help-steps">
                  <h4>How to update a task status:</h4>
                  <ol>
                    <li>Navigate to "My Tasks" in the sidebar</li>
                    <li>Find the task you want to update</li>
                    <li>Click on the status dropdown and select the new status</li>
                    <li>Add any comments if necessary</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineClipboardCheck className="card-icon" />
                <h3>Performance Reviews</h3>
              </div>
              <div className="help-card-content">
                <p>Submit and view your weekly performance reports and feedback.</p>
                <ul>
                  <li><strong>Weekly Reports:</strong> Submit reports about your weekly achievements and challenges.</li>
                  <li><strong>Feedback:</strong> View feedback from your manager on your performance.</li>
                  <li><strong>Performance Score:</strong> Track your performance scores over time.</li>
          </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>Weekly reports should be submitted by the end of each work week (Friday). You'll receive reminders for pending submissions.</p>
                </div>
              </div>
            </div>
        </div>
        )}
        
        {activeSection === 'requests' && (
        <div className="help-section">
            <h2>Requests & Claims</h2>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineCalendar className="card-icon" />
                <h3>Leave Management</h3>
              </div>
              <div className="help-card-content">
                <p>Request and manage your time off from work.</p>
                <ul>
                  <li><strong>Leave Balance:</strong> View your remaining leave days by category (vacation, sick, personal).</li>
                  <li><strong>Leave Request:</strong> Submit new leave requests with date range and reason.</li>
                  <li><strong>Request Status:</strong> Track the approval status of your leave requests.</li>
                </ul>
                <div className="help-steps">
                  <h4>How to request leave:</h4>
                  <ol>
                    <li>Navigate to "Leave Management" in the sidebar</li>
                    <li>Click on "New Leave Request"</li>
                    <li>Select the leave type, date range, and provide a reason</li>
                    <li>Submit the request for approval</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineBriefcase className="card-icon" />
                <h3>Claim Management</h3>
              </div>
              <div className="help-card-content">
                <p>Submit and track expense claims and reimbursements.</p>
                <ul>
                  <li><strong>New Claim:</strong> Submit new expense claims with receipts.</li>
                  <li><strong>Claim History:</strong> View your past claims and their status.</li>
                  <li><strong>Reimbursement:</strong> Track when approved claims will be reimbursed.</li>
                </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>Always attach clear images of receipts to expedite the approval process. Claims are typically processed within 7 business days.</p>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineGlobeAlt className="card-icon" />
                <h3>Remote Work Requests</h3>
              </div>
              <div className="help-card-content">
                <p>Request and manage remote work arrangements.</p>
                <ul>
                  <li><strong>Remote Day:</strong> Request specific days to work remotely.</li>
                  <li><strong>Extended Remote:</strong> Request longer periods of remote work (e.g., working from another location).</li>
                  <li><strong>Request Status:</strong> Track approval status of your remote work requests.</li>
          </ul>
                <div className="help-steps">
                  <h4>How to request remote work:</h4>
                  <ol>
                    <li>Navigate to "Work Remotely" in the sidebar</li>
                    <li>Select the type of remote work arrangement</li>
                    <li>Provide the requested dates and reason</li>
                    <li>Submit for approval</li>
                  </ol>
                </div>
              </div>
            </div>
        </div>
        )}
        
        {activeSection === 'admin' && isAdmin && (
        <div className="help-section">
            <h2>Admin Tools</h2>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineClipboardCheck className="card-icon" />
                <h3>Employee Management</h3>
              </div>
              <div className="help-card-content">
                <p>Manage employee records, onboarding, and offboarding processes.</p>
                <ul>
                  <li><strong>Add Employee:</strong> Create new employee profiles with all necessary details.</li>
                  <li><strong>Edit Employee:</strong> Update existing employee information.</li>
                  <li><strong>View Employees:</strong> Browse and search the employee directory.</li>
                </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>When adding a new employee, they will automatically receive an email with their login credentials.</p>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineDocument className="card-icon" />
                <h3>Document Management</h3>
              </div>
              <div className="help-card-content">
                <p>Upload and manage documents for employees.</p>
                <ul>
                  <li><strong>Upload Documents:</strong> Add new documents to employee profiles.</li>
                  <li><strong>Document Categories:</strong> Organize documents by type (ID & Visa, Employment, Miscellaneous).</li>
                  <li><strong>Document Access:</strong> Control which documents are accessible to employees.</li>
                </ul>
                <div className="help-steps">
                  <h4>How to upload a document:</h4>
                  <ol>
                    <li>Navigate to "Documents" in the admin sidebar</li>
                    <li>Select the employee from the list</li>
                    <li>Click "Add Document"</li>
                    <li>Choose the document category and file</li>
                    <li>Upload the document</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <FaFileInvoiceDollar className="card-icon" />
                <h3>Payslip Management</h3>
              </div>
              <div className="help-card-content">
                <p>Upload and manage employee payslips.</p>
                <ul>
                  <li><strong>Upload Payslips:</strong> Add new monthly payslips for employees.</li>
                  <li><strong>Manage Payslips:</strong> View, download, and delete employee payslips.</li>
                  <li><strong>Batch Upload:</strong> Upload multiple payslips in a single operation.</li>
                </ul>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>Employees will receive a notification when you upload a new payslip to their account.</p>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineChartBar className="card-icon" />
                <h3>Performance Management</h3>
              </div>
              <div className="help-card-content">
                <p>Review and provide feedback on employee performance.</p>
                <ul>
                  <li><strong>Weekly Reviews:</strong> Review employee weekly reports and provide feedback.</li>
                  <li><strong>Performance Scores:</strong> Assess employee performance on a scale of 1-20.</li>
                  <li><strong>Feedback:</strong> Provide constructive feedback to help employees improve.</li>
          </ul>
                <div className="help-steps">
                  <h4>How to review an employee report:</h4>
                  <ol>
                    <li>Navigate to "Employee Performance" in the admin sidebar</li>
                    <li>Select the employee from the list</li>
                    <li>Choose the week to review</li>
                    <li>Read the employee's report and provide a score and feedback</li>
                    <li>Save the review</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeSection === 'support' && (
          <div className="help-section">
            <h2>Support & Contact</h2>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlinePhone className="card-icon" />
                <h3>Contact HR Team</h3>
              </div>
              <div className="help-card-content">
                <p>Get in touch with the HR team for any questions or issues.</p>
                <div className="contact-info">
                  <div className="contact-item">
                    <HiOutlineMail className="contact-icon" />
                    <span>Email: <a href="mailto:hr@myelinz.com">hr@myelinz.com</a></span>
                  </div>
                  <div className="contact-item">
                    <HiOutlinePhone className="contact-icon" />
                    <span>Phone: (555) 123-4567</span>
                  </div>
                  <div className="contact-item">
                    <HiOutlinePhone className="contact-icon" />
                    <span>Internal Extension: 1001</span>
                  </div>
                </div>
                <div className="help-tip">
                  <HiOutlineExclamationCircle className="tip-icon" />
                  <p>For urgent matters outside of business hours, please contact the HR emergency line at (555) 789-0123.</p>
                </div>
              </div>
            </div>
            
            <div className="help-card">
              <div className="help-card-header">
                <HiOutlineSearch className="card-icon" />
                <h3>Frequently Asked Questions</h3>
              </div>
              <div className="help-card-content">
                <div className="faq-item">
                  <h4>How do I reset my password?</h4>
                  <p>Click on "Forgot Password" on the login page and follow the instructions sent to your email.</p>
                </div>
                <div className="faq-item">
                  <h4>When are payslips typically available?</h4>
                  <p>Payslips are usually available by the 5th of each month for the previous month's salary.</p>
                </div>
                <div className="faq-item">
                  <h4>How far in advance should I request leave?</h4>
                  <p>Regular leave should be requested at least 7 days in advance. Emergency leave can be requested at shorter notice with appropriate justification.</p>
                </div>
                <div className="faq-item">
                  <h4>Who can see my performance reviews?</h4>
                  <p>Your performance reviews are visible to you, your direct manager, and the HR team.</p>
                </div>
              </div>
            </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Help; 